/* eslint-disable max-len */
import is from 'is';
import { useState, useEffect } from 'react';
import { useEffectOnce } from '@utils/react/hooks';
import {
  Input, Button,
} from '@skyline-platform/alchemy';
import './MetadataForm.css';
import { getDeviceMetadataKeys } from '@utils/graphql/creators/metadata';
import PropTypes from 'prop-types';
import { InfoIcon } from '../../InfoIcon/InfoIcon';
import { getLogger } from '@utils/general';
import {
  extractMetadataInfo, extractMetadataPrivacy, formatMetadata, hasEmptyKeyValue, updateEditableLabel, updateValueForLabel,
} from './utils';
const logger = getLogger('src.components.FormComponents.MetadataForm');

function removeInput(prev, key) {
  const updatedObj = {
    ...prev,
  };
  delete updatedObj[key];
  return updatedObj;
}

const EditableInput = ({
  fieldKey, inputValues, setInputValues, endAdornment,
  isPrivate, onPrivacyChange, existingKeys,
}) => {
  const [titleError, setTitleError] = useState(false);
  const [valueError, setValueError] = useState(false);
  const [duplicateKeyError, setDuplicateKeyError] = useState(false);

  const checkTitleNotEmpty = (value) => {
    if (value.length === 0) {
      setTitleError(true);
    } else {
      setTitleError(false);
    }
  };

  const checkValueNotEmpty = (value) => {
    if (value.length === 0) {
      setValueError(true);
    } else {
      setValueError(false);
    }
  };

  const handleInputChange = (e, fieldKey) => {
    const { value } = e.target;

    let updatedObj = {};
    if (e.target.parentElement.classList.contains('editable-label')) {
      // if the label is updated
      updatedObj = updateEditableLabel(inputValues, fieldKey, value);
      checkTitleNotEmpty(value);
      const isDuplicate = existingKeys.some((k) => k.toLowerCase() === value.toLowerCase());
      setDuplicateKeyError(isDuplicate);
    } else if (e.target.classList.contains('base-Input-input')) {
      // if the value is updated
      updatedObj = updateValueForLabel(inputValues, fieldKey, value);
      checkValueNotEmpty(value);
    }
    setInputValues(updatedObj);
  };

  return (
    <Input
      id={`input-metadata-name-${fieldKey}`}
      editableLabel={{
        placeholder: 'Edit Title',
        onChange: (e) => handleInputChange(e, fieldKey),
        onBlur: (e) => handleInputChange(e, fieldKey),
        errorMessage: titleError ? 'Enter a title.' : duplicateKeyError ? 'Metadata label already exists.' : null,
        error: titleError || duplicateKeyError,
        endAdornment,
      }}
      helperText='Metadata Value'
      error={valueError}
      errorMessage={valueError ? 'Enter a value.' : null}
      onChange={(e) => handleInputChange(e, fieldKey)}
      onBlur={(e) => handleInputChange(e, fieldKey)}
      disabled={duplicateKeyError}
      showCheckbox
      checkboxLabel="Private"
      checkboxChecked={isPrivate}
      onCheckboxChange={() => onPrivacyChange(!isPrivate)}
      checkboxHelperText={
        <InfoIcon
          id={`private-info-${fieldKey}`}
          label='Private metadata info'
          desc='This metadata is private and can only be viewed by authorized users through the Manager API.'
        />
      }
      useWarning={true}
      warningMessage='Once you click Create New Camera, you will not be able to edit or delete this newly created metadata label.'
    />
  );
};

const EditableInputs = ({
  existingMetadataFields, setMetadata, setMetadataValid, setMetadataPrivacy,
}) => {
  // number to assign to keys to keep track of each input line
  const [key, setKey] = useState(0);
  // array of exisitng field keys assigned to each line
  const [fieldSlots, setFieldSlots] = useState([]);
  // key value pair of labels and values keyed by the field keys
  const [inputValues, setInputValues] = useState({});
  // tracks which fields are marked as private
  const [privacyValues, setPrivacyValues] = useState({});

  useEffect(() => {
    setMetadata(() => {
      const metadataToSave = extractMetadataInfo(inputValues);
      return {
        ...existingMetadataFields,
        ...metadataToSave,
      };
    });
    if (setMetadataPrivacy) {
      const newPrivacy = extractMetadataPrivacy(inputValues, privacyValues);
      setMetadataPrivacy((prev) => ({ ...prev, ...newPrivacy }));
    }
  }, [inputValues, privacyValues]);

  useEffect(() => {
    const metadataToSave = extractMetadataInfo(inputValues);
    const hasEmptyKeyVals =
      hasEmptyKeyValue(fieldSlots, inputValues)
      || fieldSlots.length !== Object.keys(metadataToSave).length;

    const editableLabels = fieldSlots
      .map((k) => Object.keys(inputValues[k] || {})[0])
      .filter(Boolean)
      .map((l) => l.toLowerCase());
    const existingLower = Object.keys(existingMetadataFields).map((k) => k.toLowerCase());
    const hasDuplicates =
      editableLabels.some((l) => existingLower.includes(l))
      || new Set(editableLabels).size !== editableLabels.length;

    setMetadataValid(!hasEmptyKeyVals && !hasDuplicates);
  }, [
    inputValues,
    fieldSlots,
    existingMetadataFields,
  ]);

  const addNewLine = () => {
    const slots = fieldSlots.concat(`additional-field-${key}`);
    setFieldSlots(slots);
    setKey((prev) => prev + 1);
  };

  const handleRemove = (keyToRemove) => {
    const updatedSlots = fieldSlots.filter((key) => key !== keyToRemove);
    setFieldSlots(updatedSlots);
    setInputValues(removeInput(inputValues, keyToRemove));
    setPrivacyValues((prev) => {
      const updated = { ...prev };
      delete updated[keyToRemove];
      return updated;
    });
  };

  const getEditableInputs = () => {
    return fieldSlots.map((key) => {
      const existingFieldKeys = Object.keys(existingMetadataFields);
      /**
        * remove all falsy values (undefined, null, empty string) from the array
        * which is what Object.keys(inputValues[k] || {})[0] returns when a slot exists in fieldSlots
        * but the user hasn't typed a label yet (so inputValues[k] is either absent or an empty object with no keys).
        */
      const otherEditableLabels = fieldSlots
        .filter((k) => k !== key)
        .map((k) => Object.keys(inputValues[k] || {})[0])
        .filter(Boolean);
      return (
        <div className='additional-field' key={key} id={key}>
          <EditableInput
            fieldKey={key}
            inputValues={inputValues}
            setInputValues={setInputValues}
            existingKeys={[...existingFieldKeys, ...otherEditableLabels]}
            isPrivate={privacyValues[key] || false}
            onPrivacyChange={(val) => setPrivacyValues((prev) => ({ ...prev, [key]: val }))}
            endAdornment={
              <button
                aria-label='Delete Additional Metadata Field'
                onClick={() => handleRemove(key)}
              >
                <i aria-hidden='true' className='fa-solid fa-trash-can' />
              </button>
            }
          />
        </div>
      );
    });
  };

  return (
    <>
      {getEditableInputs()}
      <Button
        iconLeft='fa-solid fa-circle-plus'
        label='Add new field'
        state='default'
        type='text'
        onClick={addNewLine}
      />
    </>
  );
};

export const MetadataForm = ({
  metadataFormData,
  metadataPrivacy = {},
  setMetadata,
  setMetadataValid,
  setMetadataPrivacy,
}) => {
  const [metadataFields, setMetadataFields] = useState(metadataFormData || {});

  /**
   * setMetadata causes the component to rerender, bringing the text cursor in the input
   * to the end of the text. The work around is to update the metadataFields, which sets
   * the value of the input displayed. Only when the user clicks out of the text input, then
   * setMetadata is called to set the form data, since by then the cursor will be
   * repositioned or no longer necessary anyway.
   */
  const updateMetadata = (value, field) => {
    setMetadataFields((prev) => {
      return {
        ...prev,
        [field]: value,
      };
    });
  };

  const updateFormData = (value, field) => {
    setMetadata((prev) => {
      return {
        ...prev,
        [field]: value,
      };
    });
  };

  const metadataInputs = Object.keys(metadataFields).map((field) => {
    const formattedField = field.toLowerCase().replace(/\s+/g, '-').trim();
    const isPrivate = metadataPrivacy[field] || false;
    const existingFieldInput = (
      <Input
        id={`input-${formattedField}`}
        key={`input-${formattedField}`}
        inputLabel={field}
        value={metadataFields[field] ?? ''}
        onChange={(e) => updateMetadata(e.target.value, field)}
        onBlur={(e) => updateFormData(e.target.value, field)}
        labelAdornment={isPrivate
          ? <InfoIcon
            id={`private-metadata-${formattedField}`}
            label='Private metadata info'
            desc='This metadata is private and can only be viewed by authorized users through the Manager API.'
          />
          : undefined
        }
      />
    );

    return existingFieldInput;
  });

  // Set initial form fields for metadata that already exist
  useEffectOnce(() => {
    async function populateMetadata() {
      const { execute } = getDeviceMetadataKeys();
      const { error, data } = await execute();

      if (error) {
        logger.error(error);
        return;
      }

      // TODO: from Johnny - SUGGESTION: In my opionion, it would be better to have the logic outside of the conditional and return if the data is empty or whatever you have to do
      if (!is.empty(data)) {
        const keys = data.map((m) => m.key);
        setMetadataFields(formatMetadata(keys, metadataFormData));

        if (setMetadataPrivacy) {
          const initialPrivacy = Object.fromEntries(
            data.map(({ key, private: isPrivate }) => [key, isPrivate])
          );
          setMetadataPrivacy((prev) => ({ ...prev, ...initialPrivacy }));
        }
      }
    }

    populateMetadata();
  });

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: updates field values when form data changes, no cascade risk
    setMetadataFields((prev) => formatMetadata(Object.keys(prev), metadataFormData));
  }, [metadataFormData]);

  return (
    <gar-metadata-form>
      <span>
        <h3>
          Camera Metadata
        </h3>
        <InfoIcon
          id='camera-metadata'
          label='Show description for Camera Metadata'
          desc='Add Camera Metadata fields to track additional data points such as Mile Marker or Organization.
          All Metadata fields will be published to your Stream Manager API.'
        />
      </span>
      {Object.keys(metadataFields).length > 0 && metadataInputs}
      <EditableInputs
        existingMetadataFields={metadataFields}
        setMetadata={setMetadata}
        setMetadataValid={setMetadataValid}
        setMetadataPrivacy={setMetadataPrivacy}
      />
    </gar-metadata-form>
  );
};

EditableInput.propTypes = {
  fieldKey: PropTypes.string,
  inputValues: PropTypes.object,
  setInputValues: PropTypes.func,
  endAdornment: PropTypes.node,
  isPrivate: PropTypes.bool,
  onPrivacyChange: PropTypes.func,
  existingKeys: PropTypes.arrayOf(PropTypes.string),
};

EditableInputs.propTypes = {
  existingMetadataFields: PropTypes.object,
  setMetadata: PropTypes.func,
  setMetadataValid: PropTypes.func,
  setMetadataPrivacy: PropTypes.func,
};

MetadataForm.propTypes = {
  metadataFormData: PropTypes.object,
  metadataPrivacy: PropTypes.object,
  setMetadata: PropTypes.func,
  setMetadataValid: PropTypes.func,
  setMetadataPrivacy: PropTypes.func,
};